import React, {useEffect, useState} from 'react';
import Table from '../../../components/common/table/Table';
import {Column} from "../../common/table/TableSection";
import DateRangeFilter from '../../common/table/DateRangeFilter';
import {useNavigate} from "react-router-dom";
import styles from "./AdminWorkerRequestTable.module.css";
import {RequestStatus, RequestTarget, WorkerRequestSort} from "../../../types/request";
import {WorkerRequestTableResponseData} from "../../../types/admin";
import {adminWorkerRequestTableAPI} from "../../../services/admin";
import CreateWorkerRequestByAdminPopUp from "../../../views/admin/popup/CreateWorkerRequestByAdminPopUp";
import NumberInputWithLabel from "../../common/input/NumberInputWithLabel";
import Button from "../../common/button/Button";

interface DateRange {
    start: Date | null;
    end: Date | null;
}

interface DateRanges {
    create: DateRange;
    start: DateRange;
    confirm: DateRange;
}

//어드민 작업자 추가 요청 테이블
const AdminWorkerRequestTable = () => {
    const navigate = useNavigate();
    const [data, setData] = useState<WorkerRequestTableResponseData[]>([]);
    const [isCreatePopupOpen, setIsCreatePopupOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [pendingSearchTerm, setPendingSearchTerm] = useState('');
    //필터 초기값
    const [filters, setFilters] = useState<Record<string, any>>({
        status: ['대기', '승인', '반려', '어드민'],
        create: 'ALL',
        start: 'ALL',
        confirm: 'ALL',
        min: '',
        max: ''
    });

    //날짜 필터 값
    const [dateRanges, setDateRanges] = useState<DateRanges>({
        create: {start: null, end: null},
        start: {start: null, end: null},
        confirm: {start: null, end: null}
    });

    //정렬
    const [sort, setSort] = useState<{ field: string | null, direction: 'asc' | 'desc' | null }>({
        field: 'companyName',
        direction: 'asc'
    });

    // 리셋 상태 추가
    const [isResetting, setIsResetting] = useState(false);

    // 필터 전체 초기화
    const resetAllFilters = () => {
        setIsResetting(true);

        // 필터 초기화
        setFilters({
            status: ['대기', '승인', '반려', '어드민'],
            create: 'ALL',
            start: 'ALL',
            confirm: 'ALL',
            min: '',
            max: ''
        });

        // 날짜 범위 초기화
        setDateRanges({
            create: { start: null, end: null },
            start: { start: null, end: null },
            confirm: { start: null, end: null }
        });

        // 검색 관련 초기화
        setSearchTerm('');
        setPendingSearchTerm('');

        // 페이지 초기화
        setPage(1);

        // 정렬 초기화
        setSort({
            field: 'companyName',
            direction: 'asc'
        });

        // 다음 렌더링 사이클에서 리셋 상태 해제
        setTimeout(() => setIsResetting(false), 0);
    };

    //테이블 로우 선택 시
    const handleRowClick = (row: WorkerRequestTableResponseData) => {
        //세부 페이지로 이동
        navigate(`/admin/billing/worker-request/${row.workerRequestId}`);
    };

    //컬럼
    const columns: Column<WorkerRequestTableResponseData>[] = [
        {
            key: 'companyName',
            header: '회사명',
            width: '18%',
            sortable: true
        },
        {
            key: 'workerAmount',
            header: '희망 추가인원',
            width: '18%',
            sortable: true,
            render: value => `${value.toLocaleString()}명`
        },
        {
            key: 'startAt',
            header: '희망일',
            width: '18%',
            sortable: true
        },
        {
            key: 'createdAt',
            header: '최초 요청일',
            width: '18%',
            sortable: true
        },
        {
            key: 'confirmedAt',
            header: '처리일',
            width: '18%',
            sortable: true
        },
        {
            key: 'status',
            header: '처리 상태',
            width: '10%'
        }
    ];

    //작업자 추가 요청 상태 필터
    const statusFilterOptions = [
        {label: '대기', value: '대기'},
        {label: '승인', value: '승인'},
        {label: '반려', value: '반려'},
        {label: '어드민', value: '어드민'}
    ];

    //API 사용 위한 날짜 데이터 형식 변환
    const formatDateForAPI = (date: Date | string | null): string => {
        if (!date) return '';
        const dateObj = date instanceof Date ? date : new Date(date);

        if (isNaN(dateObj.getTime())) {
            return '-';
        }
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    //API 사용 위한 날짜 범위 데이터 형식 변환
    const formatDateRangeForAPI = (start: Date | null, end: Date | null): string | null => {
        //선택 안한 경우 'ALL'로 표시
        if (!start && !end) return 'ALL';
        if (!end) return `${formatDateForAPI(start)}~`;
        //시작날짜~종료날짜 형식으로 변환
        return `${formatDateForAPI(start)}~${formatDateForAPI(end)}`;
    };

    //날짜 형식 변환
    const handleDateRangeChange = (key: string, start: Date | null, end: Date | null) => {
        // 리셋 중일 때는 날짜 변경 로직 건너뛰기
        if (isResetting) return;

        setDateRanges(prev => ({
            ...prev,
            [key]: {start, end}
        }));

        const dateRangeString = formatDateRangeForAPI(start, end);
        setFilters(prev => ({
            ...prev,
            [key]: dateRangeString
        }));

        setPage(1);
    };

    //테이블 필터 설정
    const tableFilters = [
        {
            key: 'status',
            label: '처리 상태',
            options: statusFilterOptions,
            multiple: true,
            value: filters.status,
            component: undefined
        },
        {
            key: 'workerAmount',
            label: '희망 추가인원',
            component: (
                <div className={styles.numberRangeFilter}>
                    <span>희망 추가인원: </span>
                    <div className={styles.rangeInput}>
                        <NumberInputWithLabel
                            value={filters.min}
                            onChange={(e) => handleFilterChange('min', e.target.value)}
                            min="-100"
                            placeholder="최소"
                            className={styles.numberInput}
                            title="최소 인원"
                        />
                        <span className={styles.rangeSeparator}>~</span>
                        <NumberInputWithLabel
                            value={filters.max}
                            onChange={(e) => handleFilterChange('max', e.target.value)}
                            min="0"
                            placeholder="최대"
                            className={styles.numberInput}
                            title="최대 인원"
                        />
                    </div>
                </div>
            ),
            options: [],
            multiple: false,
            value: null
        },
        {
            key: 'create',
            label: '최초 요청일',
            component: (
                <DateRangeFilter
                    label="최초 요청일"
                    startDate={dateRanges.create.start}
                    endDate={dateRanges.create.end}
                    onDateChange={(start, end) => handleDateRangeChange('create', start, end)}
                    isOpen={false}
                    onToggle={() => {
                    }}
                />
            ),
            options: [],
            multiple: false,
            value: null
        },
        {
            key: 'start',
            label: '희망일',
            component: (
                <DateRangeFilter
                    label="희망일"
                    startDate={dateRanges.start.start}
                    endDate={dateRanges.start.end}
                    onDateChange={(start, end) => handleDateRangeChange('start', start, end)}
                    isOpen={false}
                    onToggle={() => {
                    }}
                />
            ),
            options: [],
            multiple: false,
            value: null
        },
        {
            key: 'confirm',
            label: '처리일',
            component: (
                <DateRangeFilter
                    label="처리일"
                    startDate={dateRanges.confirm.start}
                    endDate={dateRanges.confirm.end}
                    onDateChange={(start, end) => handleDateRangeChange('confirm', start, end)}
                    isOpen={false}
                    onToggle={() => {
                    }}
                />
            ),
            options: [],
            multiple: false,
            value: null
        }
    ];

    //정렬 기준 설정
    const getSortParam = (field: string | null, direction: 'asc' | 'desc' | null): WorkerRequestSort => {
        if (!field || !direction) return WorkerRequestSort.CREATE_ASC;
        const sortMap: Record<string, Record<string, WorkerRequestSort>> = {
            companyName: {
                asc: WorkerRequestSort.COMPANY_ASC,
                desc: WorkerRequestSort.COMPANY_DESC
            },
            createdAt: {
                asc: WorkerRequestSort.CREATE_ASC,
                desc: WorkerRequestSort.CREATE_DESC
            },
            startAt: {
                asc: WorkerRequestSort.START_ASC,
                desc: WorkerRequestSort.START_DESC
            },
            confirmedAt: {
                asc: WorkerRequestSort.CONFIRM_ASC,
                desc: WorkerRequestSort.CONFIRM_DESC
            },
            workerAmount: {
                asc: WorkerRequestSort.AMOUNT_ASC,
                desc: WorkerRequestSort.AMOUNT_DESC
            }
        };
        return sortMap[field]?.[direction] || WorkerRequestSort.CREATE_ASC;
    };

    //검색
    const handleSearch = () => {
        setSearchTerm(pendingSearchTerm);
        setPage(1);
    };

    //엔터키 사용 검색
    const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    //테이블 데이터 설정
    const fetchData = async () => {
        try {
            setLoading(true);
            //작업자 추가 요청 데이터 API 호출
            const response = await adminWorkerRequestTableAPI({
                page: page - 1,
                sort: getSortParam(sort.field, sort.direction),
                companyName: searchTerm,
                status: filters.status as RequestStatus[],
                create: filters.create,
                start: filters.start,
                confirm: filters.confirm,
                min: filters.min,
                max: filters.max
            });
            //데이터 설정
            setData(response.content);
            //페이지 설정
            setTotalPages(Math.ceil(response.total / 7));
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [page, searchTerm, sort, filters]);

    //검색 대상 설정
    const searchOptions = [
        {value: RequestTarget.COMPANY_NAME, label: '회사명'}
    ];

    //필터값 변경
    const handleFilterChange = (key: string, value: any) => {
        setFilters(prev => {
            //상태 필터 변경
            if (key === 'status') {
                const newStatuses = value;
                if (newStatuses.length === 0) {
                    return {...prev, [key]: ['대기', '승인', '반려', '어드민']};
                }
                return {...prev, status: newStatuses};
            }

            //작업자 수 필터 변경
            if (key === 'min' || key === 'max') {
                if (value === '' || /^-?\d*$/.test(value)) {
                    const numberValue = value === '' ? '' : parseInt(value);
                    if (key === 'min' && prev.max !== '' && numberValue > parseInt(prev.max)) {
                        return {...prev, min: numberValue, max: numberValue};
                    }
                    if (key === 'max' && prev.min !== '' && numberValue !== '' && numberValue < parseInt(prev.min)) {
                        return {...prev, min: numberValue, max: numberValue};
                    }
                    return {...prev, [key]: numberValue};
                }
                return prev;
            }

            return {...prev, [key]: value};
        });
        setPage(1);
    };

    //정렬 변경
    const handleSortChange = (field: string | null, direction: 'asc' | 'desc' | null) => {
        setSort({field, direction});
    };

    return (
        <div className={styles.container}>
            <div className={styles.searchContainer}>
                <div className={styles.titleContainer}>
                    <div className={styles.padding}/>
                    <h3>작업자 추가 요청</h3>
                    <Button
                        variant="primary"
                        onClick={() => setIsCreatePopupOpen(true)}
                        size={"small"}
                        className={styles.mobileButton}
                    >
                        추가
                    </Button>
                </div>
                <div className={styles.searchInputContainer}>
                    <div>
                        {/*검색 대상 선택*/}
                        <select
                            value={"회사명"}
                            className={styles.searchTypeSelect}
                            title="검색 유형 선택"
                        >
                            {searchOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className={styles.searchInputWrapper}>
                        {/*검색 창*/}
                        <input
                            type="text"
                            placeholder="검색어를 입력하세요"
                            value={pendingSearchTerm}
                            onChange={(e) => setPendingSearchTerm(e.target.value)}
                            onKeyPress={handleKeyPress}
                            className={styles.searchInput}
                            title="검색어 입력"
                        />
                        {/*검색 버튼*/}
                        <Button
                            onClick={handleSearch}
                            className={styles.searchButton}
                            variant="primary"
                            size={"small"}
                        >
                            검색
                        </Button>
                        <Button
                            onClick={() => setIsCreatePopupOpen(true)}
                            className={styles.pcButton}
                            size={"small"}
                            variant="primary"
                        >
                            추가
                        </Button>
                    </div>
                </div>
            </div>
            {/*테이블*/}
            <Table
                columns={columns}
                data={data}
                loading={loading}
                size="medium"
                filters={tableFilters}
                showPagination={true}
                page={page}
                pageSize={7}
                totalItems={totalPages * 7}
                onPageChange={setPage}
                onFilterChange={handleFilterChange}
                onSortChange={handleSortChange}
                onRowClick={handleRowClick}
                onReset={resetAllFilters}
            />
            {/*작업자 추가 요청 생성 팝업*/}
            <CreateWorkerRequestByAdminPopUp
                isOpen={isCreatePopupOpen}
                onClose={() => {
                    setIsCreatePopupOpen(false);
                    fetchData(); // 팝업이 닫힐 때 테이블 데이터 새로고침
                }}
            />
        </div>
    );
};

export default AdminWorkerRequestTable;